// Human-friendly date formatting helpers used across the portal.
//
// formatRelative returns "5 minutes ago", "yesterday 14:32", etc. — the
// label shown by default. formatAbsolute returns a stable, locale-neutral
// "YYYY-MM-DD HH:MM:SS UTC" string used as the hover tooltip, so the user
// can see the exact timestamp without having to reach for devtools.

const ABS_FORMATTER = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
});

const TIME_HM_FORMATTER = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
});

const DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
});

function parse(iso: string): Date | null {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function formatAbsolute(iso: string): string {
    const d = parse(iso);
    if (!d) return iso;
    // Intl gives "07/05/2026, 08:02:09" — flip to ISO-ish for consistency.
    const parts = ABS_FORMATTER.formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')} UTC`;
}

// formatDate returns a bare calendar date in the BROWSER'S OWN locale
// (`undefined` locale = runtime default), e.g. "10. jun. 2026" in nb-NO or
// "Jun 10, 2026" in en-US. `dateStyle: 'medium'` always renders the month as
// a word/abbreviation, so there is no DD/MM-vs-MM/DD ambiguity regardless of
// the user's region. UTC so a date the approver picked (stored at UTC
// midnight) is shown back as the same calendar day. Used for grant
// requested/expiry dates in the approver UI.
const DATE_LOCALE_FORMATTER = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeZone: 'UTC',
});

export function formatDate(iso: string): string {
    const d = parse(iso);
    return d ? DATE_LOCALE_FORMATTER.format(d) : iso;
}

export function formatRelative(iso: string, now: Date = new Date()): string {
    const d = parse(iso);
    if (!d) return iso;

    const diffMs = now.getTime() - d.getTime();
    const future = diffMs < 0;
    const absMs = Math.abs(diffMs);
    const sec = Math.round(absMs / 1000);
    const min = Math.round(absMs / 60_000);
    const hr = Math.round(absMs / 3_600_000);

    if (sec < 45) return 'just now';
    if (min < 2) return future ? 'in a minute' : 'a minute ago';
    if (min < 60) return future ? `in ${min} minutes` : `${min} minutes ago`;
    if (hr < 2) return future ? 'in an hour' : 'an hour ago';
    if (hr < 24) return future ? `in ${hr} hours` : `${hr} hours ago`;

    // Beyond a day: switch to date-anchored phrasing. Compare UTC calendar
    // days (matches our timezone choice) so "yesterday" lines up with the
    // tooltip.
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const that = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const dayDiff = Math.round((today - that) / 86_400_000);
    const hm = TIME_HM_FORMATTER.format(d);

    if (dayDiff === 1) return `yesterday ${hm}`;
    if (dayDiff === -1) return `tomorrow ${hm}`;
    if (dayDiff > 1 && dayDiff < 7) return `${dayDiff} days ago`;
    if (dayDiff < -1 && dayDiff > -7) return `in ${-dayDiff} days`;
    return `on ${DATE_FORMATTER.format(d)}`;
}
